import api from './api';

export interface ContractorAvailability {
  _id: string;
  contractorAccountId: string;
  contractorEmail: string;
  startTime: string;
  endTime: string;
  isRecurring: boolean;
  recurringPattern?: {
    type: 'daily' | 'weekly' | 'monthly';
    interval: number;
    daysOfWeek?: number[];
    endDate?: string;
  };
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAvailabilityData {
  startTime: string;
  endTime: string;
  isRecurring?: boolean;
  recurringPattern?: {
    type: 'daily' | 'weekly' | 'monthly';
    interval: number;
    daysOfWeek?: number[];
    endDate?: string;
  };
  notes?: string;
  isActive?: boolean;
}

export interface UpdateAvailabilityData {
  startTime?: string;
  endTime?: string;
  isRecurring?: boolean;
  recurringPattern?: {
    type: 'daily' | 'weekly' | 'monthly';
    interval: number;
    daysOfWeek?: number[];
    endDate?: string;
  };
  notes?: string;
  isActive?: boolean;
}

class ContractorAvailabilityService {
  // Get contractor's own availability (requires contractor auth)
  async getMyAvailability(startDate?: string, endDate?: string): Promise<ContractorAvailability[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    // Add timestamp to prevent caching and 304 loops
    params.append('_t', Date.now().toString());

    const response = await api.get(`/contractor-availability/my-availability?${params.toString()}`);
    return response.data || [];
  }

  // Create availability slot (requires contractor auth)
  async createAvailability(data: CreateAvailabilityData): Promise<ContractorAvailability> {
    const response = await api.post('/contractor-availability/my-availability', data);
    return response.data;
  }

  // Update availability slot (requires contractor auth)
  async updateAvailability(id: string, data: UpdateAvailabilityData): Promise<ContractorAvailability> {
    const response = await api.put(`/contractor-availability/my-availability/${id}`, data);
    return response.data;
  }

  // Delete availability slot (requires contractor auth)
  async deleteAvailability(id: string): Promise<void> {
    await api.delete(`/contractor-availability/my-availability/${id}`);
  }

  // Get availability for a specific contractor (agent view)
  async getContractorAvailability(
    contractorEmail: string,
    startDate?: string,
    endDate?: string
  ): Promise<ContractorAvailability[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(
      `/contractor-availability/contractor/${encodeURIComponent(contractorEmail)}?${params.toString()}`
    );
    return response.data;
  }

  // Get availability for multiple contractors (agent view)
  async getMultipleContractorsAvailability(
    contractorEmails: string[],
    startDate?: string,
    endDate?: string
  ): Promise<Record<string, ContractorAvailability[]>> {
    const params = new URLSearchParams();
    params.append('contractorEmails', contractorEmails.join(','));
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    const response = await api.get(`/contractor-availability/contractors?${params.toString()}`);
    return response.data;
  }
}

const contractorAvailabilityService = new ContractorAvailabilityService();

export default contractorAvailabilityService;

// Explicit re-exports to ensure types are available
export type { ContractorAvailability, CreateAvailabilityData, UpdateAvailabilityData };

